import { useState, useMemo } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyBilling } from "@/hooks/useAgencyBilling";
import { useToast } from "@/hooks/use-toast";
import { ClientBillingTable } from "@/components/agency/ClientBillingTable";
import { AgencyUsageChart } from "@/components/agency/AgencyUsageChart";
import { BillingSummaryCards } from "@/components/agency/BillingSummaryCards";
import { MarkupConfigDialog } from "@/components/agency/MarkupConfigDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ClientBilling, BillingFilters } from "@/types/billing";
import {
  Settings,
  FileText,
  Download,
  RefreshCw,
  Calendar,
  Building2,
  FileSpreadsheet,
  FileJson,
  ChevronDown,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import {
  exportClientBillingCSV,
  exportUsageDataCSV,
  exportBillingReportJSON,
} from "@/lib/billing-export";

// Period options for filtering
const PERIOD_OPTIONS = [
  { value: "current", label: "Current Month" },
  { value: "last", label: "Last Month" },
  { value: "quarter", label: "Last 3 Months" },
  { value: "year", label: "Last 12 Months" },
];

// Calculate date range based on period selection
const getPeriodDates = (period: string): { start: string; end: string } => {
  const now = new Date();
  const end = now.toISOString().split("T")[0];

  let start: Date;
  switch (period) {
    case "last":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      break;
    case "quarter":
      start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      break;
    case "year":
      start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      break;
    case "current":
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  return {
    start: start.toISOString().split("T")[0],
    end,
  };
};

export default function AgencyBillingPage() {
  const { isAgency, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // Filter state
  const [period, setPeriod] = useState("current");
  const filters: BillingFilters = useMemo(() => {
    const { start, end } = getPeriodDates(period);
    return { period_start: start, period_end: end };
  }, [period]);

  // Dialog states
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientBilling | null>(null);
  const [batchInvoiceDialogOpen, setBatchInvoiceDialogOpen] = useState(false);

  // Billing data
  const {
    clients,
    summary,
    usageChart,
    markupConfigs,
    isLoading,
    updateMarkup,
    isUpdatingMarkup,
    deleteMarkup,
    isDeletingMarkup,
    generateInvoice,
    isGeneratingInvoice,
    generateBatchInvoices,
    isGeneratingBatchInvoices,
    refetch,
  } = useAgencyBilling(filters);

  // Get default markup from configs
  const defaultMarkup = useMemo(() => {
    const defaultConfig = markupConfigs.find((c) => c.tenant_id === null);
    return defaultConfig?.markup_percentage ?? 20;
  }, [markupConfigs]);

  // Get existing config for selected client
  const selectedClientConfig = useMemo(() => {
    if (!selectedClient) return null;
    return markupConfigs.find((c) => c.tenant_id === selectedClient.tenant_id) || null;
  }, [selectedClient, markupConfigs]);

  // Get list of tenant IDs that have custom (non-inherited) markup
  const customMarkupTenantIds = useMemo(() => {
    return markupConfigs
      .filter((c) => c.tenant_id !== null)
      .map((c) => c.tenant_id as string);
  }, [markupConfigs]);

  // Handle invoice generation
  const handleGenerateInvoice = (client: ClientBilling) => {
    const { start, end } = getPeriodDates(period);
    generateInvoice(
      { tenant_id: client.tenant_id, period_start: start, period_end: end },
      {
        onSuccess: () => {
          toast({
            title: "Invoice Generated",
            description: `Invoice for ${client.tenant_name} has been created.`,
          });
        },
        onError: (error: Error) => {
          toast({
            title: "Failed to generate invoice",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  // Handle batch invoice generation
  const handleBatchInvoices = () => {
    const { start, end } = getPeriodDates(period);
    // Get tenant IDs for clients with unbilled amounts
    const tenantIds = clients.filter(c => c.unbilled_amount > 0).map(c => c.tenant_id);
    generateBatchInvoices(
      { tenant_ids: tenantIds, period_start: start, period_end: end },
      {
        onSuccess: () => {
          toast({
            title: "Batch Invoices Generated",
            description: "Invoices have been created for all clients with unbilled usage.",
          });
          setBatchInvoiceDialogOpen(false);
        },
        onError: (error: Error) => {
          toast({
            title: "Failed to generate batch invoices",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  // Handle markup save
  const handleSaveMarkup = (config: Parameters<typeof updateMarkup>[0]) => {
    updateMarkup(config, {
      onSuccess: () => {
        toast({
          title: "Markup Updated",
          description: selectedClient
            ? `Markup for ${selectedClient.tenant_name} has been updated.`
            : "Default markup has been updated.",
        });
        setMarkupDialogOpen(false);
        setSelectedClient(null);
      },
      onError: (error: Error) => {
        toast({
          title: "Failed to update markup",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  // Handle reset to inherited (delete custom markup)
  const handleResetToDefault = (tenantId: string) => {
    deleteMarkup(tenantId, {
      onSuccess: () => {
        toast({
          title: "Markup Reset",
          description: selectedClient
            ? `${selectedClient.tenant_name} is now using inherited markup settings.`
            : "Markup has been reset to default.",
        });
        setMarkupDialogOpen(false);
        setSelectedClient(null);
      },
      onError: (error: Error) => {
        toast({
          title: "Failed to reset markup",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  // Handle export - billing summary CSV
  const handleExportSummary = () => {
    const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || period;
    exportClientBillingCSV(clients, summary, { periodLabel });
    toast({
      title: "Export Complete",
      description: "Client billing summary has been downloaded.",
    });
  };

  // Handle export - detailed CSV with usage breakdown
  const handleExportDetailed = () => {
    const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || period;
    exportClientBillingCSV(clients, summary, { periodLabel, includeUsageDetails: true });
    toast({
      title: "Export Complete",
      description: "Detailed billing report has been downloaded.",
    });
  };

  // Handle export - usage chart data
  const handleExportUsageData = () => {
    const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || period;
    exportUsageDataCSV(usageChart, { periodLabel });
    toast({
      title: "Export Complete",
      description: "Usage data has been downloaded.",
    });
  };

  // Handle export - full JSON report
  const handleExportJSON = () => {
    const periodLabel = PERIOD_OPTIONS.find(o => o.value === period)?.label || period;
    exportBillingReportJSON(clients, summary, usageChart, { periodLabel });
    toast({
      title: "Export Complete",
      description: "Full billing report (JSON) has been downloaded.",
    });
  };

  // Redirect if not an agency
  if (!authLoading && !isAgency()) {
    return <Navigate to="/" replace />;
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Building2 className="h-8 w-8" />
              Agency Billing
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage client billing, markup, and generate invoices.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Period Selector */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[160px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Actions */}
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={clients.length === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportSummary}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Billing Summary (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportDetailed}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Detailed Report (CSV)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportUsageData}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Usage Data (CSV)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportJSON}>
                  <FileJson className="h-4 w-4 mr-2" />
                  Full Report (JSON)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={() => {
                setSelectedClient(null);
                setMarkupDialogOpen(true);
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              Default Markup
            </Button>

            <Button
              onClick={() => setBatchInvoiceDialogOpen(true)}
              disabled={!clients.some((c) => c.unbilled_amount > 0)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Invoices
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <BillingSummaryCards summary={summary} isLoading={isLoading} />

        {/* Usage Chart */}
        <AgencyUsageChart
          data={usageChart}
          isLoading={isLoading}
          title="Aggregate Usage"
          description={`Usage costs across all clients (${PERIOD_OPTIONS.find((o) => o.value === period)?.label})`}
        />

        {/* Client Billing Table */}
        <Card>
          <CardHeader>
            <CardTitle>Client Billing</CardTitle>
            <CardDescription>
              View and manage billing for individual clients. Click a row to view details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ClientBillingTable
              clients={clients}
              onGenerateInvoice={handleGenerateInvoice}
              onConfigureMarkup={(client) => {
                setSelectedClient(client);
                setMarkupDialogOpen(true);
              }}
              isGeneratingInvoice={isGeneratingInvoice}
              customMarkupTenantIds={customMarkupTenantIds}
              defaultMarkup={defaultMarkup}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        {/* Markup Configuration Dialog */}
        <MarkupConfigDialog
          open={markupDialogOpen}
          onOpenChange={(open) => {
            setMarkupDialogOpen(open);
            if (!open) setSelectedClient(null);
          }}
          client={selectedClient}
          existingConfig={selectedClientConfig}
          defaultMarkup={defaultMarkup}
          onSave={handleSaveMarkup}
          onResetToDefault={handleResetToDefault}
          isSaving={isUpdatingMarkup || isDeletingMarkup}
        />

        {/* Batch Invoice Confirmation Dialog */}
        <AlertDialog open={batchInvoiceDialogOpen} onOpenChange={setBatchInvoiceDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate Batch Invoices</AlertDialogTitle>
              <AlertDialogDescription>
                This will create invoices for all clients with unbilled usage for the selected
                period. Are you sure you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBatchInvoices}
                disabled={isGeneratingBatchInvoices}
              >
                {isGeneratingBatchInvoices ? "Generating..." : "Generate Invoices"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
